import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as eventFilesService from '@/lib/services/event-files';
import type { EventFilesSyncInput } from '@/lib/types/event-files.types';
import { eventKeys } from '@/lib/hooks/useEvents';

export const eventFileKeys = {
  all: ['event-files'] as const,
  lists: () => [...eventFileKeys.all, 'list'] as const,
  list: (eventId: number) => [...eventFileKeys.lists(), eventId] as const,
  counts: (eventIds: number[]) =>
    [...eventFileKeys.all, 'counts', eventIds.slice().sort()] as const,
};

export function useEventFiles(eventId: number) {
  return useQuery({
    queryKey: eventFileKeys.list(eventId),
    queryFn: () => eventFilesService.getEventFiles(eventId),
    enabled: !!eventId,
  });
}

export function useEventFileCounts(eventIds: number[]) {
  return useQuery({
    queryKey: eventFileKeys.counts(eventIds),
    queryFn: () => eventFilesService.getEventFileCounts(eventIds),
    enabled: eventIds.length > 0,
  });
}

export function useEventFileOpenUrl() {
  return useMutation({
    mutationFn: eventFilesService.getEventFileOpenUrl,
  });
}

export function useSyncEventFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: EventFilesSyncInput) =>
      eventFilesService.syncEventFiles(input),
    onSuccess: (data, input) => {
      queryClient.setQueryData(eventFileKeys.list(input.eventId), data);
      queryClient.invalidateQueries({
        queryKey: eventFileKeys.list(input.eventId),
      });
      queryClient.invalidateQueries({ queryKey: eventFileKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}
